import { useCallback, useEffect } from 'react';
import type { MutableRefObject } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';

import { DEBOUNCED_SEND_INTERVAL } from '@pages/Admin/Campaign/ui/constants';
import { CreateCampaignFormValue } from '@features/Admin/Campaign/model/type';
import {
  hasUnsavedCampaignChanges,
  mapCampaignDtoToFormValues,
} from '@features/Admin/Campaign/utils/edit-campaign-mapper';
import { CampaignDto } from '@shared/services/apiService/apiService';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';

interface UseCampaignAutosaveOptions {
  campaignData?: CampaignDto;
  formMethods: UseFormReturn<CreateCampaignFormValue>;
  saveCampaignForm: (data: CreateCampaignFormValue) => Promise<boolean>;
  leadListChangedRef: MutableRefObject<boolean>;
  setLeadListChanged: (changed: boolean) => void;
  refetch: () => Promise<unknown>;
  forceValidate: () => void;
}

export const useCampaignAutosave = ({
  campaignData,
  formMethods,
  saveCampaignForm,
  leadListChangedRef,
  setLeadListChanged,
  refetch,
  forceValidate,
}: UseCampaignAutosaveOptions) => {
  const { control, reset, trigger } = formMethods;
  const formData = useWatch({ control });

  const notify = useCallback(() => {
    toast.success(CustomSuccessToast, {
      closeButton: false,
      autoClose: 5000,
      icon: false,
    });
  }, []);

  const debouncedSubmit = useCallback(
    debounce(async () => {
      const currentFormData = formMethods.getValues();

      if (!campaignData || !currentFormData.id) {
        return;
      }

      if (!hasUnsavedCampaignChanges(currentFormData, campaignData, leadListChangedRef.current)) {
        return;
      }

      const isSaved = await saveCampaignForm(currentFormData);
      if (!isSaved) {
        return;
      }

      notify();
      setLeadListChanged(false);
      await refetch();
    }, DEBOUNCED_SEND_INTERVAL),
    [campaignData, formMethods, notify, refetch, saveCampaignForm, setLeadListChanged],
  );

  useEffect(() => {
    if (!campaignData) return;

    reset(mapCampaignDtoToFormValues(campaignData));
  }, [campaignData, reset]);

  useEffect(() => {
    if (campaignData) {
      trigger().then(() => {
        forceValidate();

        const currentFormData = formMethods.getValues();

        if (
          currentFormData.id &&
          hasUnsavedCampaignChanges(currentFormData, campaignData, leadListChangedRef.current)
        ) {
          debouncedSubmit();
        }
      });
    }

    return () => debouncedSubmit.cancel();
  }, [
    campaignData,
    debouncedSubmit,
    forceValidate,
    formData,
    formMethods,
    leadListChangedRef,
    trigger,
  ]);

  return { debouncedSubmit };
};
